export default class InterviewThreadViewportController {

    private inputExpanded = false;
    private shouldFocusInput = false;
    private shouldFocusInputOnMobile = false;
    private shouldScrollToBottom = false;
    private pendingAgentMessageId: number|null = null;
    private focusAnimationFrameId: number|null = null;
    private scrollAnimationFrameId: number|null = null;
    private messagesContainer: HTMLDivElement|null = null;
    private agentMessageMediaListenerCleanups: Array<() => void> = [];
    private readonly resizeHandler = (): void => this.syncHeight();

    constructor(
        private readonly component: HTMLElement,
        private readonly isInputDisabled: () => boolean,
    ) {
    }

    public start(): void {
        this.syncHeight();
        window.addEventListener("resize", this.resizeHandler);
        window.addEventListener("scroll", this.resizeHandler, {passive: true});
        window.visualViewport?.addEventListener("resize", this.resizeHandler);
        window.visualViewport?.addEventListener("scroll", this.resizeHandler, {passive: true});
    }

    public destroy(): void {
        window.removeEventListener("resize", this.resizeHandler);
        window.removeEventListener("scroll", this.resizeHandler);
        window.visualViewport?.removeEventListener("resize", this.resizeHandler);
        window.visualViewport?.removeEventListener("scroll", this.resizeHandler);
        if (this.focusAnimationFrameId !== null) {
            window.cancelAnimationFrame(this.focusAnimationFrameId);
        }
        if (this.scrollAnimationFrameId !== null) {
            window.cancelAnimationFrame(this.scrollAnimationFrameId);
        }
        this.pendingAgentMessageId = null;
        this.shouldScrollToBottom = false;
        this.messagesContainer = null;
        this.clearAgentMessageMediaListeners();
    }

    public isExpanded(): boolean {
        return this.inputExpanded;
    }

    public expandInput(): void {
        this.inputExpanded = true;
        if (window.innerWidth < 768) {
            this.requestScrollToBottom();
        }
    }

    public collapseInput(): void {
        this.inputExpanded = false;
    }

    public bindMessagesContainer(element: HTMLDivElement): void {
        this.messagesContainer = element;
        if (this.pendingAgentMessageId !== null) {
            this.scrollToAgentMessage();
            return;
        }
        this.scrollToBottomIfNeeded();
    }

    public requestScrollToBottom(): void {
        this.shouldScrollToBottom = true;
    }

    public requestScrollToAgentMessage(messageId: number): void {
        this.clearAgentMessageMediaListeners();
        this.pendingAgentMessageId = messageId;
        this.shouldScrollToBottom = false;
    }

    public requestFocus(allowMobile: boolean): void {
        this.shouldFocusInput = true;
        this.shouldFocusInputOnMobile = allowMobile;
    }

    public focusInputIfRequested(element: HTMLTextAreaElement): void {
        if (!this.shouldFocusInput || this.isInputDisabled()) {
            return;
        }

        const canFocus = this.shouldFocusInputOnMobile || window.innerWidth >= 768;
        this.shouldFocusInput = false;
        this.shouldFocusInputOnMobile = false;
        if (!canFocus) {
            return;
        }

        if (this.focusAnimationFrameId !== null) {
            window.cancelAnimationFrame(this.focusAnimationFrameId);
        }
        this.focusAnimationFrameId = window.requestAnimationFrame(() => {
            this.focusAnimationFrameId = null;
            if (this.isInputDisabled() || (!this.inputExpanded && window.innerWidth < 768)) {
                return;
            }
            element.focus({preventScroll: true});
            this.syncHeight();
        });
    }

    private syncHeight(): void {
        const visualViewport = window.visualViewport;
        const viewportHeight = visualViewport?.height ?? window.innerHeight;
        const viewportOffsetTop = visualViewport?.offsetTop ?? 0;
        const top = Math.max(0, this.component.getBoundingClientRect().top - viewportOffsetTop);
        const height = Math.max(0, Math.floor(viewportHeight - top));
        const heightChanged = this.component.style.height !== `${height}px`;
        this.component.classList.toggle("interview-thread-root-viewport-constrained", height < 420);
        this.component.style.height = `${height}px`;

        if (heightChanged && this.inputExpanded) {
            this.scheduleScrollToBottom();
        }
    }

    private scheduleScrollToBottom(): void {
        if (window.innerWidth >= 768) {
            return;
        }

        this.shouldScrollToBottom = true;
        if (this.scrollAnimationFrameId !== null) {
            window.cancelAnimationFrame(this.scrollAnimationFrameId);
        }
        this.scrollAnimationFrameId = window.requestAnimationFrame(() => {
            this.scrollAnimationFrameId = null;
            this.scrollToBottomIfNeeded();
        });
    }

    private scrollToBottomIfNeeded(): void {
        if (!this.shouldScrollToBottom || this.messagesContainer === null) {
            return;
        }

        this.clearAgentMessageMediaListeners();
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
        this.shouldScrollToBottom = false;
    }

    private scrollToAgentMessage(): void {
        if (this.pendingAgentMessageId === null || this.messagesContainer === null) {
            return;
        }

        const messageId = this.pendingAgentMessageId;
        this.pendingAgentMessageId = null;
        this.shouldScrollToBottom = false;
        const messagesContainer = this.messagesContainer;
        const message = messagesContainer.querySelector<HTMLElement>(
            `[data-interview-message-id="${messageId}"]`,
        );
        if (message === null) {
            return;
        }

        this.watchAgentMessageMedia(message, messagesContainer);
        this.scrollAgentMessageIntoView(message, messagesContainer);
    }

    private scrollAgentMessageIntoView(
        message: HTMLElement,
        messagesContainer: HTMLDivElement,
    ): void {
        if (messagesContainer.scrollHeight <= messagesContainer.clientHeight) {
            return;
        }

        const messageRect = message.getBoundingClientRect();
        const top = messageRect.height <= messagesContainer.clientHeight
            ? messagesContainer.scrollHeight
            : messageRect.top
                - messagesContainer.getBoundingClientRect().top
                + messagesContainer.scrollTop;
        messagesContainer.scrollTo({top, behavior: "smooth"});
    }

    private watchAgentMessageMedia(
        message: HTMLElement,
        messagesContainer: HTMLDivElement,
    ): void {
        const mediaElements = message.querySelectorAll<HTMLElement>("img, audio, video");
        mediaElements.forEach(mediaElement => {
            if (!this.isMediaPending(mediaElement)) {
                return;
            }

            const loadedEvent = mediaElement.tagName === "IMG" ? "load" : "loadedmetadata";
            let active = true;
            const cleanup = (): void => {
                if (!active) {
                    return;
                }
                active = false;
                mediaElement.removeEventListener(loadedEvent, onSettled);
                mediaElement.removeEventListener("error", onSettled);
            };
            const onSettled = (): void => {
                if (!active) {
                    return;
                }
                cleanup();
                this.agentMessageMediaListenerCleanups = this.agentMessageMediaListenerCleanups
                    .filter(listenerCleanup => listenerCleanup !== cleanup);
                if (this.messagesContainer === messagesContainer && messagesContainer.contains(message)) {
                    this.scrollAgentMessageIntoView(message, messagesContainer);
                }
            };

            mediaElement.addEventListener(loadedEvent, onSettled);
            mediaElement.addEventListener("error", onSettled);
            this.agentMessageMediaListenerCleanups.push(cleanup);
            if (!this.isMediaPending(mediaElement)) {
                onSettled();
            }
        });
    }

    private isMediaPending(mediaElement: HTMLElement): boolean {
        if (mediaElement.tagName === "IMG") {
            return !(mediaElement as HTMLImageElement).complete;
        }

        return (mediaElement as HTMLMediaElement).readyState < 1;
    }

    private clearAgentMessageMediaListeners(): void {
        this.agentMessageMediaListenerCleanups.forEach(cleanup => cleanup());
        this.agentMessageMediaListenerCleanups = [];
    }

}
